import { useState, type FormEvent } from "react";
import type { PartnerTier } from "@tih/shared";
import { postPartner } from "../../api/client";
import { useBotProtection } from "../../hooks/useBotProtection";
import { HoneypotField } from "./HoneypotField";
import { TurnstileWidget } from "./TurnstileWidget";

type State = "idle" | "loading" | "ok" | "err";

export function PartnerForm({
  tier,
  defaultAmountUgx,
  onSuccess,
}: {
  tier: PartnerTier;
  defaultAmountUgx: number;
  onSuccess?: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [amountUgx, setAmountUgx] = useState(defaultAmountUgx);
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState<string | null>(null);
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!ready) return;
    setState("loading");
    setError(null);
    try {
      await postPartner({ tier, name, email, amountUgx, ...botFields });
      setState("ok");
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not record gift");
      setState("err");
      resetBotProtection();
    }
  }

  if (state === "ok") {
    return (
      <div className="form-success">
        <div className="sec-eyebrow"><em>thank you</em></div>
        <h3 className="sec-title sec-title-sm">We'll be in touch.</h3>
        <p className="lede-serif">A team member will email you payment details within a day.</p>
      </div>
    );
  }

  return (
    <form className="form-stack" onSubmit={onSubmit}>
      <HoneypotField value={website} onChange={setWebsite} />
      <label>
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required disabled={state === "loading"} />
      </label>
      <label>
        <span>Email</span>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={state === "loading"} />
      </label>
      <label>
        <span>Amount (UGX)</span>
        <input
          type="number"
          min={1000}
          step={1000}
          value={amountUgx}
          onChange={(e) => setAmountUgx(Math.max(1000, Number(e.target.value) || defaultAmountUgx))}
          disabled={state === "loading"}
        />
      </label>
      {needsTurnstile && config?.turnstileSiteKey && (
        <TurnstileWidget
          siteKey={config.turnstileSiteKey}
          onToken={onTurnstileToken}
          onExpire={onTurnstileExpire}
          onRegisterReset={registerTurnstileReset}
        />
      )}
      <button type="submit" className="btn btn-primary" disabled={state === "loading" || !ready}>
        {state === "loading" ? "Submitting…" : "Pledge gift"}
      </button>
      {error && <div className="form-error">{error}</div>}
    </form>
  );
}
